import { NFLGame, GameStatus, GameStatistic, GameTimelineEvent } from '@/types';

const BASE_URL = 'https://site.api.espn.com/apis/site/v2/sports/football/nfl';

const cache = new Map<string, { data: unknown; ts: number }>();

function cached<T>(key: string, ttl: number, fetcher: () => Promise<T>): Promise<T> {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.ts < ttl) return Promise.resolve(entry.data as T);
  return fetcher().then((data) => {
    cache.set(key, { data, ts: Date.now() });
    return data;
  });
}

function mapStatus(event: any): GameStatus {
  const state = event.status?.type?.state ?? '';
  const name = event.status?.type?.name ?? '';
  if (state === 'post' || event.status?.type?.completed) return 'finished';
  if (state === 'in') {
    if (name === 'STATUS_HALFTIME') return 'halftime';
    return 'live';
  }
  return 'scheduled';
}

const STAT_LABELS: Record<string, string> = {
  firstDowns: 'Primeiras descidas',
  totalYards: 'Jardas totais',
  netPassingYards: 'Jardas de passe',
  rushingYards: 'Jardas corridas',
  yardsPerPlay: 'Jardas por jogada',
  thirdDownEff: '3ª descida',
  fourthDownEff: '4ª descida',
  totalPenaltiesYards: 'Penalidades',
  turnovers: 'Turnovers',
  possessionTime: 'Tempo de posse',
};

const STAT_ORDER = [
  'firstDowns',
  'totalYards',
  'netPassingYards',
  'rushingYards',
  'yardsPerPlay',
  'thirdDownEff',
  'fourthDownEff',
  'totalPenaltiesYards',
  'turnovers',
  'possessionTime',
];

function statNumber(value: string): number | undefined {
  const parsed = Number.parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function mapTeamStats(summary: any): GameStatistic[] | undefined {
  const teams: any[] = summary?.boxscore?.teams ?? [];
  const home = teams.find((t) => t.homeAway === 'home');
  const away = teams.find((t) => t.homeAway === 'away');
  if (!home || !away) return undefined;

  const homeStats = new Map((home.statistics ?? []).map((s: any) => [s.name, s.displayValue]));
  const awayStats = new Map((away.statistics ?? []).map((s: any) => [s.name, s.displayValue]));

  const rows: GameStatistic[] = [];
  STAT_ORDER.forEach((name) => {
    const homeValue = homeStats.get(name);
    const awayValue = awayStats.get(name);
    if (homeValue == null && awayValue == null) return;
    rows.push({
      id: name,
      label: STAT_LABELS[name] ?? name,
      home: String(homeValue ?? '-'),
      away: String(awayValue ?? '-'),
      homeValue: statNumber(String(homeValue ?? '')),
      awayValue: statNumber(String(awayValue ?? '')),
    });
  });

  return rows.length > 0 ? rows : undefined;
}

function scoringType(play: any): GameTimelineEvent['type'] {
  const text = `${play.scoringType?.name ?? ''} ${play.type?.text ?? ''}`.toLowerCase();
  if (text.includes('touchdown')) return 'touchdown';
  if (text.includes('field')) return 'field_goal';
  if (text.includes('safety')) return 'safety';
  if (text.includes('extra')) return 'extra_point';
  return 'score';
}

function mapScoringPlays(summary: any, homeId: string, awayId: string): GameTimelineEvent[] | undefined {
  const plays: any[] = summary?.scoringPlays ?? [];
  const scoring = plays.map((play): GameTimelineEvent => {
    const teamId = String(play.team?.id ?? '');
    const periodNumber = play.period?.number;
    return {
      id: String(play.id),
      side: teamId === homeId ? 'home' : teamId === awayId ? 'away' : 'center',
      minute: play.clock?.displayValue,
      period: periodNumber ? `Q${periodNumber}` : undefined,
      type: scoringType(play),
      title: play.scoringType?.displayName ?? play.type?.text ?? 'Pontuação',
      detail: play.text,
      score: {
        home: Number(play.homeScore ?? 0),
        away: Number(play.awayScore ?? 0),
      },
    };
  });

  if (scoring.length === 0) return undefined;

  const periods = new Set(scoring.map((play) => play.period).filter(Boolean));
  const markers: GameTimelineEvent[] = Array.from(periods).map((period) => ({
    id: `period-${period}`,
    side: 'center',
    period,
    type: 'period_start',
    title: `Início do ${period}`,
  }));

  return [...markers, ...scoring];
}

function mapGame(event: any, summary?: any): NFLGame {
  const comp = event.competitions?.[0] ?? event;
  const competitors = comp.competitors ?? event.competitors ?? [];
  const home = competitors.find((c: any) => c.homeAway === 'home');
  const away = competitors.find((c: any) => c.homeAway === 'away');
  const status = event.status ?? comp.status;
  const homeId = String(home?.team?.id ?? '');
  const awayId = String(away?.team?.id ?? '');

  // Quarter scores
  const homeLines: any[] = home?.linescores ?? [];
  const awayLines: any[] = away?.linescores ?? [];

  // Quarter from status detail e.g. "Q3 - 7:42"
  const detail = status?.type?.shortDetail ?? '';
  const quarterMatch = detail.match(/Q(\d)/);
  const quarter = quarterMatch ? parseInt(quarterMatch[1], 10) : undefined;

  return {
    id: String(event.id),
    sport: 'nfl',
    homeTeam: {
      id: homeId,
      name: home?.team?.displayName ?? home?.team?.name ?? '',
      shortName: home?.team?.abbreviation ?? '',
      logo: home?.team?.logo ?? '',
    },
    awayTeam: {
      id: awayId,
      name: away?.team?.displayName ?? away?.team?.name ?? '',
      shortName: away?.team?.abbreviation ?? '',
      logo: away?.team?.logo ?? '',
    },
    score: {
      home: parseInt(home?.score ?? '0', 10),
      away: parseInt(away?.score ?? '0', 10),
    },
    status: mapStatus(event),
    quarter,
    clock: status?.displayClock !== '0:00' ? status?.displayClock : undefined,
    startTime: event.date ?? comp.startDate ?? '',
    week: comp.week?.number ?? undefined,
    timeline: summary ? mapScoringPlays(summary, homeId, awayId) : undefined,
    statistics: summary ? mapTeamStats(summary) : undefined,
  };
}

export async function getNFLTodayGames(): Promise<NFLGame[]> {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return cached(`espn:nfl:today:${today}`, 30_000, async () => {
    const res = await fetch(`${BASE_URL}/scoreboard?dates=${today}`);
    if (!res.ok) throw new Error(`ESPN NFL HTTP ${res.status}`);
    const json = await res.json();
    const events: any[] = json.events ?? [];
    console.log(`[ESPN NFL] Jogos hoje: ${events.length}`);
    return events.map((event) => mapGame(event));
  });
}

export async function getNFLLiveGames(): Promise<NFLGame[]> {
  const games = await getNFLTodayGames();
  return games.filter((g) => g.status === 'live');
}

export async function getNFLWeekGames(week?: number): Promise<NFLGame[]> {
  const season = new Date().getFullYear();
  const weekParam = week ? `&week=${week}` : '';
  return cached(`espn:nfl:week:${season}:${week ?? 'current'}`, 120_000, async () => {
    const res = await fetch(`${BASE_URL}/scoreboard?season=${season}${weekParam}`);
    if (!res.ok) throw new Error(`ESPN NFL HTTP ${res.status}`);
    const json = await res.json();
    const events: any[] = json.events ?? [];
    console.log(`[ESPN NFL] Jogos semana ${week ?? 'atual'}: ${events.length}`);
    return events.map((event) => mapGame(event));
  });
}

export async function getNFLGameDetail(gameId: string): Promise<NFLGame | null> {
  return cached(`espn:nfl:game:${gameId}`, 30_000, async () => {
    const res = await fetch(`${BASE_URL}/summary?event=${gameId}`);
    if (!res.ok) return null;
    const json = await res.json();
    const event = json.header?.competitions?.[0];
    if (!event) return null;
    return mapGame({ ...json.header, competitions: [event] }, json);
  });
}
